import db from "../db/database";
import {
  fetchFromBusinessCentral,
  parseImportResponse,
  sendToBusinessCentral,
} from "../utils/businessCentralUtils";
import { formatAxiosError } from "../utils/common";
import { getPendingSync, isOnline } from "./syncService";
import { getTPVByNoOrId } from "./tpvService";
import { tiendaDelNumero } from "../utils/numeroDeCajaDeBC";
import { getActiveEnvironment } from "./environmentService";
import { leerEntidad } from "./entidadDeBusinessCentral";
import { aperturasDeLaFila, conExplicacionDelRechazo, horaAlMinuto } from "./reaperturaDelDia";
import {
  conAvisoDeBajaRechazada,
  enviarLote,
  motivoDeGuardadoFallido,
  resumenDelLote,
  separarPendientes,
} from "./loteSyncHelpers";
import {
  anotarAviso,
  anotarError,
  anotarFila,
  anotarInicioDeLote,
  anotarPaso,
  anotarResumenDelLote,
} from "./syncLog";

// Nombre con el que esta entidad aparece en las líneas de consola.
const ENTIDAD = "aperturas de caja";

// Función para verificar si un turno tiene No asignado
const getTurnoNo = async (turnoId) => {
  try {
    // Primero buscamos el turno por su nombre
    const turno = await db.turnos
      .filter(
        (t) => (Number(t.No) === Number(turnoId) || Number(t.id) === Number(turnoId)) && !t.deleted
      )
      .first();

    if (!turno) {
      anotarAviso(ENTIDAD, `turno "${turnoId}" no encontrado`);
      return null;
    }

    // Si el turno ya está sincronizado (pendingSync = 0) y tiene No, lo utilizamos
    if (turno.pendingSync === 0 && turno.No !== undefined && turno.No !== null) {
      return turno.No;
    }

    // Si el turno aún no está sincronizado o no tiene No, retornamos null
    return null;
  } catch (error) {
    anotarError(ENTIDAD, `falló la búsqueda del turno "${turnoId}"`, error);
    return null;
  }
};

// Función para transformar una apertura de caja al formato esperado por Business Central
// Exportada para poder probarla: ver el comentario de transformCajaForBC.
export const transformAperturaForBC = async (apertura) => {
  // Obtener el No del turno (si existe)
  let turnoNo = await getTurnoNo(apertura.Turno);

  // Solo incluimos la apertura si el turno tiene No asignado
  if (turnoNo === undefined || turnoNo === null) {
    anotarAviso(
      ENTIDAD,
      `no enviada (id ${apertura.id}) — el turno "${apertura.Turno}" aún no tiene No de Business Central`
    );
    return null;
  }

  // Transformar la apertura al formato esperado por BC
  // Una sola expresión para las tres grafías que acepta BC. Antes eran tres
  // independientes: si alguien tocaba una y olvidaba las otras, BC se quedaba
  // con la primera no vacía sin avisar de la divergencia.
  const codigo = apertura.No || "TEMP";

  // Si la tienda no está sincronizada todavía no tiene No de BC: se manda vacío
  // y BC vuelve a deducirla del código de caja, que es el comportamiento de
  // antes. No se corta el envío por esto -- la apertura sigue siendo válida.
  let tiendaNo = "";

  if (apertura.TPV) {
    try {
      const tienda = await getTPVByNoOrId(apertura.TPV);
      tiendaNo = tienda?.No || "";
    } catch (error) {
      anotarAviso(
        ENTIDAD,
        `no se pudo resolver la tienda ${apertura.TPV} (id ${apertura.id}) — ${
          error?.message || error
        }`
      );
    }
  }

  const transformedApertura = {
    No: codigo,
    No_: codigo,
    "No.": codigo,
    Cajero: apertura.Cajero,
    FechaDeApertura: apertura.FechaDeApertura,
    HoraDeApertura: apertura.HoraDeApertura || "00:00", // Añadimos la hora de apertura
    ImporteDeApertura: apertura.ImporteDeApertura,
    Estado: apertura.Estado,
    Turno: turnoNo, // Usamos el No del turno, no su nombre
    Caja: apertura.Caja || "", // Añadimos el No de la caja
    // La tienda, resuelta al No de Business Central.
    //
    // Hace falta porque la clave de una apertura en BC es (tienda, caja, fecha).
    // Sin ella, BC deducía la tienda buscando el código de caja, que sólo es
    // único DENTRO de su tienda: si el mismo código existiera en dos, se quedaba
    // con la primera que apareciera. Desde la extensión 1.0.4.35 eso se rechaza
    // por ambiguo en vez de acertar por casualidad. Hoy en el ambiente los 16
    // códigos de caja son únicos, así que no se dispara -- pero deja de ser una
    // bomba de espera en cuanto alguien repita un código en otra tienda.
    //
    // Se resuelve con getTPVByNoOrId porque `apertura.TPV` puede traer dos cosas
    // según de dónde venga el registro: el código de BC si la caja llegó por el
    // pull o si se dedujo del No ("TIENDA;CAJA;FECHA"), o el id local de Dexie
    // si la caja se creó desde el formulario. BC necesita el código, no el id.
    Tienda: tiendaNo,
    Deleted: apertura.deleted,
  };

  // Crear el formato final esperado por el endpoint
  return {
    data: JSON.stringify({ Aperturas: [transformedApertura] }),
  };
};

// Payload con todas las aperturas que se manden en la misma llamada.
//
// Desde la extensión 1.0.4.35, insertaAperturaCaja procesa cada apertura aislada
// en TryProcesarApertura y devuelve un resultado por registro. Antes el array
// entero iba dentro de una sola try function, así que un fallo abortaba las
// demás -- por eso este lote no se podía hacer.
//
// transformAperturaForBC es asíncrona (resuelve el turno y la tienda) y puede
// devolver null cuando el turno todavía no tiene No de BC. Esas aperturas no
// viajan: se informan aparte, porque no es un rechazo de BC sino una
// precondición que aún no se cumple.
const transformAperturasForBC = async (aperturas) => {
  const transformadas = [];
  const omitidas = [];

  for (const apertura of aperturas) {
    const payload = await transformAperturaForBC(apertura);

    if (!payload) {
      omitidas.push(apertura);
      continue;
    }

    transformadas.push(JSON.parse(payload.data).Aperturas[0]);
  }

  return {
    payload: { data: JSON.stringify({ Aperturas: transformadas }) },
    enviadas: aperturas.filter((apertura) => !omitidas.includes(apertura)),
    omitidas,
  };
};

// Nombre con el que se identifica una apertura en el cuadro de resultados.
const nombreDeApertura = (apertura) =>
  [apertura.Caja, apertura.FechaDeApertura].filter(Boolean).join(" · ") ||
  apertura.No ||
  `Apertura ${apertura.id}`;

// BC rechazó la baja: se revierte el borrado local para que la apertura vuelva
// a aparecer en el listado y local y BC no queden divergentes. Mismo criterio
// que en el resto de los servicios de sincronización.
const restoreDeletedApertura = async (apertura, reason) => {
  await db.aperturaDeCaja.update(apertura.id, {
    deleted: false,
    pendingSync: 0,
    lastModified: new Date(),
    // El motivo que devolvió BC se guarda en el propio registro. Es lo único
    // que sobrevive a la corrida: el registro reaparece en el listado con
    // pendingSync 0, o sea con aspecto de estar perfectamente sincronizado, y
    // sin esto no queda nadie que pueda explicar por qué volvió. Lo lee
    // RecordSyncTag. Ver SANTPV-59.
    syncError: reason || "Business Central no aplicó la baja.",
  });
};

// Sincroniza una sola apertura de caja a Business Central. Devuelve
// { ok, message }, la misma firma que el resto de los maestros -- antes usaba
// { success, message } y recibía el registro entero en vez del id.
// Anota por qué Business Central rechazó el alta de una apertura. Si no se
// puede escribir, el rechazo sigue saliendo en el resultado del envío.
const guardarMotivoDelRechazo = async (aperturaId, motivo) => {
  try {
    await db.aperturaDeCaja.update(aperturaId, { motivoDelRechazo: motivo });
  } catch {
    // Sin el motivo guardado sólo se pierde la explicación en el cierre.
  }
};

// syncAperturasDeCaja (bulk) la reutiliza por cada pendiente; el botón por
// fila del listado también la llama para sincronizar sólo esa apertura, sin
// tocar el resto de las pendientes.
export const syncAperturaDeCajaById = async (aperturaId) => {
  if (!isOnline()) {
    return { ok: false, message: "Sin conexión a internet" };
  }

  try {
    const apertura = await db.aperturaDeCaja.get(aperturaId);
    if (!apertura) {
      return { ok: false, message: "Apertura de caja no encontrada" };
    }

    if (apertura.deleted) {
      // Nunca llegó a BC: sólo existe en local.
      if (!apertura.No) {
        await db.aperturaDeCaja.delete(apertura.id);
        return { ok: true, message: "Apertura eliminada" };
      }

      const transformedData = await transformAperturaForBC(apertura);
      if (!transformedData) {
        return {
          ok: false,
          message:
            "El turno de esta apertura todavía no tiene número asignado por Business Central",
        };
      }

      const response = await sendToBusinessCentral(
        "Importaciones_insertaAperturaCaja",
        transformedData,
        true
      );
      const result = parseImportResponse(response, apertura.No);

      if (!result.ok) {
        await restoreDeletedApertura(apertura, result.message);
        return {
          ok: false,
          message:
            result.message ||
            "Business Central no aplicó la baja. La apertura sigue en el listado.",
        };
      }

      await db.aperturaDeCaja.delete(apertura.id);
      return { ok: true, message: "Apertura de caja dada de baja en Business Central" };
    }

    // Transformar la apertura para Business Central (verifica si el turno tiene No)
    const transformedData = await transformAperturaForBC(apertura);

    // Si no se pudo transformar, el turno todavía no tiene No asignado
    if (!transformedData) {
      return {
        ok: false,
        message: "El turno de esta apertura todavía no tiene número asignado por Business Central",
      };
    }

    // Enviar apertura individual a Business Central
    const response = await sendToBusinessCentral(
      "Importaciones_insertaAperturaCaja",
      transformedData,
      true
    );
    const result = parseImportResponse(response, apertura.No || "TEMP");

    if (!result.ok || !result.assignedNo) {
      // Los dos rechazos de la reapertura (`APERTURA_DEL_DIA_ABIERTA` y
      // `APERTURA_CERRADA`) llevan delante qué significan. La apertura sigue con
      // pendingSync en 1 y se reintenta. Acuerdo
      // `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H6 · TPV.
      const motivo =
        conExplicacionDelRechazo(result.message) || "Respuesta inválida de Business Central";

      // Se guarda el motivo para que el cierre de esta apertura lo pueda decir:
      // si no, el cierre sólo sabe que a la apertura le falta el número.
      // Campo propio y no `syncError`, que en la apertura marca una baja
      // rechazada.
      await guardarMotivoDelRechazo(apertura.id, motivo);

      return { ok: false, message: motivo };
    }

    anotarPaso(ENTIDAD, `guardado · No ${result.assignedNo} (id ${apertura.id})`);

    // Se lee antes del update, que es justo lo que lo borra.
    const teniaBajaRechazada = Boolean(apertura.syncError);

    await db.aperturaDeCaja.update(apertura.id, {
      No: result.assignedNo,
      lastModified: new Date(),
      lastSynced: new Date(),
      pendingSync: 0,
      syncError: null,
      motivoDelRechazo: null,
    });

    return {
      ok: true,
      message: conAvisoDeBajaRechazada(
        "Apertura de caja sincronizada correctamente.",
        teniaBajaRechazada
      ),
    };
  } catch (error) {
    anotarError(ENTIDAD, `falló el envío del registro ${aperturaId}`, error);
    // BC rechaza con HTTP 400 y el motivo real dentro de
    // error.response.data.error.message.
    const motivo = formatAxiosError(error, "No se pudo sincronizar la apertura de caja");
    if (error?.response?.status === 400) await guardarMotivoDelRechazo(aperturaId, motivo);
    return { ok: false, message: motivo };
  }
};

export const syncAperturasDeCaja = async () => {
  if (!isOnline()) {
    return false;
  }

  // Aperturas cuyo push a BC falló en esta corrida. Antes los errores sólo se
  // escribían en consola y la función devolvía true igual, así que un rechazo
  // de BC pasaba por sincronización completada.
  const failedAperturas = [];

  // Una fila por registro enviado, para el cuadro que se le muestra a quien
  // apretó Sincronizar. Incluye los correctos, no sólo los fallos.
  const detalle = [];

  try {
    const pendingAperturasDeCaja = await getPendingSync("aperturaDeCaja");

    if (pendingAperturasDeCaja.length > 0) {
      const { soloLocales, aEnviar } = separarPendientes(pendingAperturasDeCaja);

      for (const apertura of soloLocales) {
        await db.aperturaDeCaja.delete(apertura.id);
        detalle.push(
          anotarFila(
            ENTIDAD,
            {
              registro: nombreDeApertura(apertura),
              no: null,
              accion: "deleted",
              ok: true,
              mensaje: "Eliminada sólo en local: nunca llegó a Business Central",
            },
            apertura.id,
            "baja-local"
          )
        );
      }

      if (aEnviar.length > 0) {
        const { payload, enviadas, omitidas } = await transformAperturasForBC(aEnviar);

        // Las que no se pudieron armar no viajan, pero sí se cuentan: su turno
        // todavía no tiene número de BC, y eso hay que decirlo.
        for (const apertura of omitidas) {
          const motivo =
            "El turno de esta apertura todavía no tiene número asignado por Business Central";
          failedAperturas.push({
            apertura,
            action: apertura.deleted ? "delete" : "save",
            reason: motivo,
          });
          detalle.push(
            anotarFila(
              ENTIDAD,
              {
                registro: nombreDeApertura(apertura),
                no: apertura.No || null,
                accion: "error",
                ok: false,
                mensaje: motivo,
              },
              apertura.id,
              "no-enviada"
            )
          );
        }

        if (enviadas.length > 0) {
          anotarInicioDeLote(ENTIDAD, enviadas.length);

          const lote = await enviarLote(
            "Importaciones_insertaAperturaCaja",
            payload,
            enviadas.length,
            "aperturas de caja"
          );

          // El cruce es por posición: BC devuelve los resultados en el mismo
          // orden en que recibió los registros, y así está comentado en su bucle.
          // Por No_ no se podría, porque todas las altas se mandan como "TEMP".
          for (let indice = 0; indice < enviadas.length; indice++) {
            const apertura = enviadas[indice];
            const resultado = lote.results[indice];
            let traiaBajaRechazada = false;

            if (apertura.deleted) {
              if (resultado.ok) {
                await db.aperturaDeCaja.delete(apertura.id);
              } else {
                await restoreDeletedApertura(apertura, resultado.message);
                failedAperturas.push({
                  apertura,
                  action: "delete",
                  reason: resultado.message,
                });
              }
            } else if (resultado.ok && resultado.assignedNo) {
              // Se anota antes del update, que es lo que borra el motivo anterior.
              traiaBajaRechazada = Boolean(apertura.syncError);

              await db.aperturaDeCaja.update(apertura.id, {
                No: resultado.assignedNo,
                lastModified: new Date(),
                lastSynced: new Date(),
                pendingSync: 0,
                syncError: null,
                motivoDelRechazo: null,
              });
            } else {
              // Un guardado rechazado no se escribe en local, así que la apertura
              // sigue pendiente y se reintenta. Los dos rechazos de la reapertura
              // (H6) no son la excepción: sólo cambia el texto, que lleva delante
              // qué significa.
              const motivo = conExplicacionDelRechazo(motivoDeGuardadoFallido(resultado));
              await guardarMotivoDelRechazo(apertura.id, motivo);
              failedAperturas.push({ apertura, action: "save", reason: motivo });
            }

            detalle.push(
              anotarFila(
                ENTIDAD,
                {
                  registro: nombreDeApertura(apertura),
                  no: resultado.assignedNo || apertura.No || null,
                  accion: resultado.action || (apertura.deleted ? "deleted" : "saved"),
                  ok: apertura.deleted
                    ? resultado.ok
                    : resultado.ok && Boolean(resultado.assignedNo),
                  mensaje: conAvisoDeBajaRechazada(
                    conExplicacionDelRechazo(resultado.message || ""),
                    traiaBajaRechazada
                  ),
                },
                apertura.id,
                apertura.deleted ? "baja" : undefined
              )
            );
          }
        }
      }
    }

    // Cierra el lote en consola con el mismo recuento que ve quien apretó

    // Sincronizar. Antes había que sumar las líneas a ojo.

    const resumen = resumenDelLote(detalle);

    anotarResumenDelLote(ENTIDAD, resumen);

    if (failedAperturas.length > 0) {
      const deletes = failedAperturas.filter((f) => f.action === "delete");
      const saves = failedAperturas.filter((f) => f.action === "save");

      const parts = [];
      if (deletes.length > 0) {
        parts.push(
          `${deletes.length} apertura(s) de caja no se pudieron eliminar en Business Central y siguen en el listado`
        );
      }
      if (saves.length > 0) {
        parts.push(
          `${saves.length} apertura(s) de caja no se pudieron guardar en Business Central`
        );
      }

      const reason = failedAperturas.find((f) => f.reason)?.reason;

      return {
        success: false,
        failedCount: failedAperturas.length,
        message: reason ? `${parts.join(". ")}: ${reason}` : parts.join(". "),
        lote: resumen,
      };
    }

    // Se devuelve el detalle también cuando todo salió bien: la pantalla muestra
    // el mismo cuadro en los dos casos. `executeWithRetry` sólo mira
    // `success === false`, e InvoiceForm descarta el resultado a propósito, así
    // que un objeto acá no cambia nada para ninguno de los dos.
    return { success: true, lote: resumen };
  } catch (error) {
    anotarError(ENTIDAD, "falló la sincronización", error);
    return false;
  }
};

// Trae el histórico de aperturas de caja desde BC (OData "AperturaDeCaja") y
// las agrega localmente. Nunca pasa por addAperturaDeCaja (que tiene reglas
// de negocio: bloquea si ya hay caja abierta, marca como caja activa si
// Estado === "Abierto", dispara sync de stock) -- inserta directo con
// db.aperturaDeCaja.bulkAdd, igual que fetchAndMapCierresDeCaja hace con los
// cierres, para que un histórico nunca pueda "abrir" una caja por accidente.
// Dedup: por No|HoraDeApertura contra lo ya sincronizado, y por clave natural
// (Caja|Fecha|Hora) contra lo pendiente de sincronizar localmente, mismo
// patrón que ya usa el pull de cierres de caja en useSyncHistoricos.js.
//
// Una fila con reapertura trae dos aperturas del mismo No (ver
// aperturasDeLaFila). Por eso la clave ya no es el No solo: con ella, de un día
// con reapertura llegaba una sola apertura, y el cierre de la otra se quedaba
// sin pareja en matchAperturasToCierres. Las horas se comparan al minuto porque
// el terminal guarda `HH:mm` y BC devuelve `HH:mm:ss`. Acuerdo
// `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H6 · TPV.
export const pullAperturasFromBC = async ({ fechaInicio, fechaFin, caja, turno } = {}) => {
  if (!isOnline()) {
    return { success: false, nuevas: 0, encontradas: 0, message: "Sin conexión a internet" };
  }

  try {
    const filter = [];
    if (fechaInicio) filter.push(`FechaDeApertura ge ${fechaInicio}`);
    if (fechaFin) filter.push(`FechaDeApertura le ${fechaFin}`);
    if (caja) filter.push(`Caja eq '${caja}'`);
    if (turno) filter.push(`Turno eq ${turno}`);

    // El nombre del servicio no es el mismo en todos los ambientes: en
    // desarrollo es «AperturaDeCaja» y producción lo publica como «Apertura».
    // Se prueban los dos y se recuerda el que conteste, en vez de tenerlo
    // escrito fijo y descubrir en producción que el histórico no llega.
    const ambiente = (await getActiveEnvironment())?.env;
    const serverAperturas = await leerEntidad({
      entidad: "aperturas",
      ambiente,
      leer: (nombre) => fetchFromBusinessCentral(nombre, filter.join(" and ")),
    });

    const localAperturas = await db.aperturaDeCaja.toArray();
    const claveDeBC = (no, hora) => `${no}|${horaAlMinuto(hora) || "00:00"}`;
    const claveNatural = (caja, fecha, hora) => `${caja}|${fecha}|${horaAlMinuto(hora) || "00:00"}`;

    const existingKeys = new Set(
      localAperturas.filter((a) => a.No).map((a) => claveDeBC(a.No, a.HoraDeApertura))
    );
    const pendingKeys = new Set(
      localAperturas
        .filter((a) => !a.No)
        .map((a) => claveNatural(a.Caja, a.FechaDeApertura, a.HoraDeApertura))
    );

    const now = new Date();
    const nuevas = [];

    for (const bc of serverAperturas) {
      const no = bc.No || bc["No."] || bc.No_ || null;

      // Esta entidad de BC no trae un campo TPV separado -- el nombre de
      // tienda solo viaja embebido en el "No" (formato "TPV;Caja;Fecha"),
      // mismo patrón que ya usa el pull de cierres de caja en
      // useSyncHistoricos.js. Si en algún momento BC empieza a mandar TPV
      // explícito, ese tiene prioridad.
      const tpvFromNo = tiendaDelNumero(no);

      for (const apertura of aperturasDeLaFila(bc)) {
        if (no) {
          const clave = claveDeBC(no, apertura.HoraDeApertura);
          if (existingKeys.has(clave)) continue;
          existingKeys.add(clave);
        } else if (
          pendingKeys.has(claveNatural(bc.Caja, bc.FechaDeApertura, apertura.HoraDeApertura))
        ) {
          continue;
        }

        nuevas.push({
          No: no,
          Cajero: apertura.Cajero,
          FechaDeApertura: bc.FechaDeApertura,
          HoraDeApertura: apertura.HoraDeApertura,
          ImporteDeApertura: apertura.ImporteDeApertura,
          Estado: apertura.Estado,
          Turno: bc.Turno,
          Caja: bc.Caja,
          TPV: bc.TPV || tpvFromNo || null,
          createdAt: now,
          pendingSync: 0,
          lastModified: now,
          lastSynced: now,
          deleted: false,
        });
      }
    }

    if (nuevas.length) {
      await db.aperturaDeCaja.bulkAdd(nuevas);
    }

    return { success: true, nuevas: nuevas.length, encontradas: serverAperturas.length };
  } catch (error) {
    anotarError(ENTIDAD, "falló el histórico", error);
    return { success: false, nuevas: 0, encontradas: 0, message: error.message };
  }
};
